/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { Button, Card, Empty, Form, InputNumber, Input, Modal, Space, Table, Tag, Typography, message } from 'antd';
import {
  useGetLiveAssetsQuery,
  useGetLiveAssetReservationsQuery,
  useReserveLiveAssetMutation,
  useReleaseLiveAssetReservationMutation,
} from '../../api/liveAssetsApi';
import { useTranslation } from 'react-i18next';
import type { LiveAsset } from '../../types/inventory';
import { toastError } from '../../utils/toast';

const { Text } = Typography;

const LiveAssetsSection: React.FC<{ saveId: string }> = ({ saveId }) => {
  const { t } = useTranslation('inventory');
  const { data: assets = [], isLoading } = useGetLiveAssetsQuery({ saveId });
  const { data: reservations = [] } = useGetLiveAssetReservationsQuery({ saveId, status: 'open' });
  const [reserve, { isLoading: reserving }] = useReserveLiveAssetMutation();
  const [release, { isLoading: releasing }] = useReleaseLiveAssetReservationMutation();

  const [target, setTarget] = useState<LiveAsset | null>(null);
  const [form] = Form.useForm();

  const submit = async () => {
    if (!target) return;
    try {
      const v = await form.validateFields();
      await reserve({ saveId, plantingId: target.plantingId, payload: { quantity: v.quantity, reason: v.reason, counterpartyRef: v.counterpartyRef } }).unwrap();
      message.success(t('liveAssets.reserved'));
      setTarget(null);
      form.resetFields();
    } catch (e: any) {
      if (e?.errorFields) return;
      toastError(e);
    }
  };

  const doRelease = async (reservationId: string) => {
    try {
      await release({ saveId, reservationId }).unwrap();
      message.success(t('liveAssets.released'));
    } catch (e: any) {
      toastError(e);
    }
  };

  const resvByPlanting = new Map<string, typeof reservations>();
  for (const r of reservations) {
    const list = resvByPlanting.get(r.planting_id) || [];
    list.push(r);
    resvByPlanting.set(r.planting_id, list);
  }

  return (
    <Card title={t('liveAssets.title')} loading={isLoading}>
      {assets.length === 0 ? (
        <Empty description={t('liveAssets.empty')} />
      ) : (
        <Table
          rowKey="plantingId"
          size="small"
          dataSource={assets}
          pagination={false}
          columns={[
            {
              title: t('liveAssets.crop'), key: 'crop',
              render: (_: unknown, a: LiveAsset) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{a.cropName || t('liveAssets.plantingFallback')}</Text>
                  {a.cropLatinName && <Text type="secondary" style={{ fontSize: 12 }}>{a.cropLatinName}</Text>}
                </Space>
              ),
            },
            { title: t('liveAssets.total'), dataIndex: 'quantity', key: 'q', align: 'right' as const },
            { title: t('liveAssets.reserve'), dataIndex: 'reserved', key: 'r', align: 'right' as const },
            { title: t('liveAssets.available'), key: 'a', align: 'right' as const, render: (_: unknown, a: LiveAsset) => <Text strong>{a.available}</Text> },
            {
              title: t('common:labels.actions'), key: 'act',
              render: (_: unknown, a: LiveAsset) => {
                const resv = resvByPlanting.get(a.plantingId) || [];
                return (
                  <Space wrap size={4}>
                    <Button size="small" type="primary" disabled={a.available <= 0} onClick={() => { form.resetFields(); setTarget(a); }}>
                      {t('liveAssets.reserve')}
                    </Button>
                    {resv.map((r) => (
                      <Tag key={r.id} closable closeIcon={t('liveAssets.release')} onClose={(e) => { e.preventDefault(); doRelease(r.id); }} color="gold">
                        {t('units.pcsValue', { count: Number(r.quantity) })}
                      </Tag>
                    ))}
                  </Space>
                );
              },
            },
          ]}
        />
      )}

      <Modal
        open={!!target}
        title={t('liveAssets.modalTitle', { crop: target?.cropName || '' })}
        onOk={submit}
        onCancel={() => setTarget(null)}
        confirmLoading={reserving || releasing}
        okText={t('liveAssets.doReserve')}
        cancelText={t('common:actions.cancel')}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="quantity" label={t('liveAssets.quantityAvailable', { available: target?.available ?? 0 })} rules={[{ required: true, message: t('liveAssets.quantityRequired') }, { type: 'number', min: 1, message: t('liveAssets.min1') }]}>
            <InputNumber min={1} max={target?.available} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reason" label={t('liveAssets.reasonOptional')}><Input placeholder={t('liveAssets.reasonPlaceholder')} /></Form.Item>
          <Form.Item name="counterpartyRef" label={t('liveAssets.counterpartyOptional')}><Input /></Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default LiveAssetsSection;
